import type { PaginationModel } from '@montugroup/design-system';
import type { SortingState } from '@tanstack/react-table';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import useGetPharmacistReplenishmentOrders from '@/hooks/useGetPharmacistReplenishmentOrders';
import type { ReplenishmentOrderItemDto } from '@montugroup/circuit-api-contracts';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import { noOp } from '@montugroup/circuit-shared/utils/noOp';
import { useQuery } from '@tanstack/react-query';

const DEFAULT_PAGE_SIZE = 50;
const INITIAL_PAGE = 0;

const {
  listReplenishmentOrderItems: { keys },
} = getTsPharmacyClient();

export type PharmacistReplenishmentOrdersData = {
  pageSize: number;
  handlePaginationModelChange: (model: PaginationModel) => void;
  handleSortingOrderChange: (sortOrder: SortingState) => void;

  replenishmentOrderItems: ReplenishmentOrderItemDto[];
  loading: boolean;
  shouldResetPageIndex?: boolean;
  setShouldResetPageIndex: (value: boolean) => void;
  beforeCursor: string | null;
  afterCursor: string | null;
  endDateFilter: Date | null;
  startDateFilter: Date | null;
  setEndDateFilter: (endDateFilter: Date | null) => void;
  setStartDateFilter: (startDateFilter: Date | null) => void;
};

export const PharmacistReplenishmentOrdersContext = createContext<PharmacistReplenishmentOrdersData>({
  pageSize: DEFAULT_PAGE_SIZE,
  handlePaginationModelChange: noOp,
  handleSortingOrderChange: noOp,
  replenishmentOrderItems: [],
  loading: false,
  shouldResetPageIndex: false,
  setShouldResetPageIndex: noOp,
  beforeCursor: null,
  afterCursor: null,
  endDateFilter: null,
  startDateFilter: null,
  setEndDateFilter: noOp,
  setStartDateFilter: noOp,
});

export function PharmacistReplenishmentOrdersProvider(props: PropsWithChildren) {
  const { children } = props;
  const {
    paginationModel: { pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({
    page: INITIAL_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder({
    initialSortingOrder: { name: 'replenishmentOrderId', reverse: true },
  });
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null);
  const [afterCursor, setAfterCursor] = useState<string | null>(null);

  const [searchCursor, setSearchCursor] = useState<{ after?: string; before?: string } | null>(null);

  const { data, mutateAsync, isPending: isReplenishmentOrderItemsLoading } = useGetPharmacistReplenishmentOrders();

  const { data: replenishmentOrderItemsData } = useQuery<typeof data>({
    queryKey: [
      ...keys,
      endDateFilter,
      startDateFilter,
      searchCursor?.after,
      searchCursor?.before,
      pageSize,
      sortingOrder.name,
      sortingOrder.reverse,
    ],
    queryFn: async () => {
      return mutateAsync({
        body: {
          filter: {
            ...(startDateFilter ? { startDate: startDateFilter.toISOString() } : {}),
            ...(endDateFilter ? { endDate: endDateFilter.toISOString() } : {}),
          },
          page: {
            size: pageSize,
            ...searchCursor,
          },
          ...(['createdAt', 'replenishmentOrderId'].includes(sortingOrder.name)
            ? {
                sort: [
                  {
                    field: sortingOrder.name as 'createdAt' | 'replenishmentOrderId',
                    direction: sortingOrder.reverse ? 'desc' : 'asc',
                  },
                ],
              }
            : {}),
        },
        headers: { authorization: getAuthBearerToken() },
      });
    },
  });

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => {
      if (model.pageSize !== pageSize) {
        setShouldResetPageIndex(true);
        setSearchCursor(null);
      }
      if (model.page > 0 && afterCursor) {
        setSearchCursor({ after: afterCursor });
      } else if (model.page < 0 && beforeCursor) {
        setSearchCursor({ before: beforeCursor });
      }

      setPaginationModel(model);
    },
    [setPaginationModel, setShouldResetPageIndex, pageSize, afterCursor, beforeCursor],
  );

  useEffect(() => {
    const { next, prev } = replenishmentOrderItemsData?.body.meta.page?.cursor ?? {};
    setBeforeCursor(prev ?? null);
    setAfterCursor(next ?? null);
  }, [replenishmentOrderItemsData?.body.meta.page?.cursor]);

  useEffect(() => {
    setShouldResetPageIndex(true);
    setSearchCursor(null);
  }, [endDateFilter, startDateFilter, sortingOrder, setShouldResetPageIndex]);

  const value = useMemo(
    () => ({
      pageSize,
      replenishmentOrderItems: replenishmentOrderItemsData?.body.data ?? [],
      loading: isReplenishmentOrderItemsLoading,
      endDateFilter,
      setEndDateFilter,
      startDateFilter,
      setStartDateFilter,
      handlePaginationModelChange,
      handleSortingOrderChange,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      beforeCursor,
      afterCursor,
    }),
    [
      pageSize,
      replenishmentOrderItemsData?.body.data,
      isReplenishmentOrderItemsLoading,
      endDateFilter,
      startDateFilter,
      handlePaginationModelChange,
      handleSortingOrderChange,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      beforeCursor,
      afterCursor,
    ],
  );

  return (
    <PharmacistReplenishmentOrdersContext.Provider value={value}>
      {children}
    </PharmacistReplenishmentOrdersContext.Provider>
  );
}

export const usePharmacistReplenishmentOrders = () => {
  const {
    pageSize,
    replenishmentOrderItems,
    loading,
    endDateFilter,
    startDateFilter,
    handlePaginationModelChange,
    handleSortingOrderChange,
    setEndDateFilter,
    setStartDateFilter,
    shouldResetPageIndex,
    setShouldResetPageIndex,
    beforeCursor,
    afterCursor,
  } = useContext(PharmacistReplenishmentOrdersContext);

  return {
    pageSize,
    replenishmentOrderItems,
    endDateFilter,
    startDateFilter,
    loading,
    handlePaginationModelChange,
    handleSortingOrderChange,
    setEndDateFilter,
    setStartDateFilter,
    shouldResetPageIndex,
    setShouldResetPageIndex,
    beforeCursor,
    afterCursor,
  };
};
